import { NavigationBarItem } from '@/components/NavigationBar/types';
import { navigationPaths } from '@/constants/navigationPaths';

export type TurbineFromLocationState = {
  siteId: string;
  locationName: string;
};

type TurbineNavigationParams = {
  name?: string | null;
  model?: string | null;
};

export const buildTurbineQueryParams = ({
  name,
  model
}: TurbineNavigationParams): string => {
  const params = new URLSearchParams();

  if (name) {
    params.set('name', name);
  }

  if (model) {
    params.set('model', model);
  }

  const query = params.toString();
  return query ? `?${query}` : '';
};

export const buildTurbineQueryParamsFromSearchParams = (
  searchParams: URLSearchParams
): string => {
  return buildTurbineQueryParams({
    name: searchParams.get('name'),
    model: searchParams.get('model')
  });
};

export const buildTurbineBreadcrumb = ({
  turbineName,
  turbineId,
  fromLocation
}: {
  turbineName: string;
  turbineId?: string;
  fromLocation?: TurbineFromLocationState | null;
}): NavigationBarItem[] => {
  const displayName = turbineName?.trim() || '';
  const shouldShowTurbineName =
    Boolean(displayName) &&
    (!turbineId || displayName.toLowerCase() !== turbineId.toLowerCase());

  if (fromLocation?.siteId && fromLocation?.locationName) {
    const crumbs: NavigationBarItem[] = [
      { label: 'Locations', path: navigationPaths.locations },
      {
        label: fromLocation.locationName,
        path: `${navigationPaths.locations}/${fromLocation.siteId}?name=${encodeURIComponent(fromLocation.locationName)}`
      }
    ];

    if (shouldShowTurbineName) {
      crumbs.push({ label: displayName, path: '' });
    }

    return crumbs;
  }

  const crumbs: NavigationBarItem[] = [
    { label: 'Turbines', path: navigationPaths.turbines }
  ];

  if (shouldShowTurbineName) {
    crumbs.push({ label: displayName, path: '' });
  }

  return crumbs;
};

export const isLocationTurbineBreadcrumb = (
  items: NavigationBarItem[]
): boolean => items.length >= 2 && items[0]?.path === navigationPaths.locations;

const TURBINE_BREADCRUMB_MAX_LENGTH = 7;
const LOCATIONS_BREADCRUMB_LABEL = 'Locations';
const LOCATIONS_BREADCRUMB_SHORT_LABEL = 'Loc...';
const BREADCRUMB_ELLIPSIS = '...';
const LOCATION_TURBINE_BREADCRUMB_TOTAL_MAX_LENGTH = 36;

type LocationTurbineBreadcrumbLabels = {
  locationsLabel: string;
  locationNameLabel: string;
  turbineLabel: string;
};

const formatTurbineLabel = (label: string, maxLength: number): string => {
  if (label.length <= maxLength) {
    return label;
  }

  return `${label.slice(0, maxLength)}${BREADCRUMB_ELLIPSIS}`;
};

const formatLocationsLabel = (turbineOverflow: number): string => {
  if (turbineOverflow <= 0) {
    return LOCATIONS_BREADCRUMB_LABEL;
  }

  return LOCATIONS_BREADCRUMB_SHORT_LABEL;
};

export const formatLocationTurbineBreadcrumbLabels = ({
  locationNameLabel,
  turbineLabel
}: {
  locationNameLabel: string;
  turbineLabel: string;
}): LocationTurbineBreadcrumbLabels => {
  const turbineOverflow = Math.max(
    0,
    turbineLabel.length - TURBINE_BREADCRUMB_MAX_LENGTH
  );

  let locationsLabel = formatLocationsLabel(turbineOverflow);
  let turbineMaxLength = TURBINE_BREADCRUMB_MAX_LENGTH;
  let formattedTurbineLabel = formatTurbineLabel(
    turbineLabel,
    turbineMaxLength
  );

  while (
    locationsLabel.length +
      locationNameLabel.length +
      formattedTurbineLabel.length >
      LOCATION_TURBINE_BREADCRUMB_TOTAL_MAX_LENGTH &&
    turbineMaxLength > 1
  ) {
    turbineMaxLength -= 1;
    formattedTurbineLabel = formatTurbineLabel(turbineLabel, turbineMaxLength);
  }

  return {
    locationsLabel,
    locationNameLabel,
    turbineLabel: formattedTurbineLabel
  };
};
